import { useState, useRef, useMemo } from "react"
import Image from "next/image"
import { MainLayout } from "components/Layout"
import { useQuery } from "@tanstack/react-query"
import { GetServerSideProps } from "next"
import { useRouter } from "next/router"

import SectionV2 from "components/Sections/SectionV2"
import EventFilteration from "components/atoms/EventFiltration/eventFiltration.index"
import AppDownloadModal from "components/download_the_app/AppDownloadModal"

import getEvents from "api/event/fetchEvents"

import { IFinalEvent } from "types/event"
import { AllEventsResponse } from "pages/events"
import { useLang } from "hooks/useLang"

/* ---------------------------------- SSR ---------------------------------- */

interface AllEventsProps {
    data: AllEventsResponse | null
    error: string | null
}

export const getServerSideProps: GetServerSideProps<AllEventsProps> = async ({
    query,
}) => {
    try {
        const params = new URLSearchParams(query as any).toString()

        const res = await fetch(
            `${process.env.NEXT_PUBLIC_API_URL}events${params ? `?${params}` : ""}`
        )

        const data = await res.json()

        return { props: { data, error: null } }
    } catch (e) {
        return { props: { data: null, error: "Failed to load events" } }
    }
}

/* -------------------------------- Component ------------------------------- */

const DiscoverPage = ({ data }: AllEventsProps) => {
    const router = useRouter()
    const [showAppModal, setShowAppModal] = useState(false)
    const heroRef = useRef<HTMLDivElement | null>(null)
    const { isAr, t } = useLang()

    /* --------------------- FILTERS FROM URL (ONLY SOURCE) -------------------- */

    const filters = useMemo(() => {
        const { date, price, category, city, sort } = router.query

        return {
            date: typeof date === "string" ? date : undefined,
            price: typeof price === "string" ? price : undefined,
            category: typeof category === "string" ? category : undefined,
            city: typeof city === "string" ? city : undefined,
            sort: typeof sort === "string" ? sort : undefined,
        }
    }, [router.query])

    /* ------------------------------- API QUERY ------------------------------ */

    const queryKey = useMemo(() => ["events", filters], [filters])

    const {
        data: events,
        isLoading,
        isFetching,
    } = useQuery({
        queryKey,
        queryFn: () => getEvents(filters),
        initialData: data ?? undefined,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    })

    const eventsPayload: IFinalEvent[] = events?.payload ?? []

    /* -------------------------------- Render -------------------------------- */

    return (
        <>
            <MainLayout>
                {/* Background */}
                <div
                    ref={heroRef}
                    className="fixed left-0 top-0 -z-10 md:aspect-video h-full md:max-h-screen w-full overflow-hidden md:block"
                >
                    <Image
                        src="/images/discover-bg.png"
                        alt="Discover background"
                        fill
                        className="absolute w-full min-h-screen object-cover"
                        style={{ zIndex: -200, opacity: 0.8 }}
                        priority
                    />
                    <div className="absolute w-full min-h-screen bg-primary_blue/65 blur-xl" />
                </div>

                <div className="flex flex-col mt-[150px] md:mt-[50px]">
                    <div>
                        {/* Promo Banner */}
                        <div className="md:flex max-w-[1180px] mx-auto px-4 md:px-0">
                            {/* <div className="w-full overflow-hidden bg-[#49B4FF] py-[25px] flex items-center justify-between rounded-[16px] relative">
                <div className="px-4 flex items-center gap-3 z-[20] flex-1">
                  <img
                    src="/svg/passtickets.svg"
                    className="w-[50px] h-[50px]"
                    alt=""
                  />
                  <span className="text-4 font-bold uppercase">
                    {t("enjoy_better_exp")}
                  </span>
                </div>

                <div className="px-4 flex items-center justify-end h-full flex-1">
                  <div
                    onClick={() => setShowAppModal(true)}
                    className="px-4 py-1 z-[2] rounded-full bg-[#FC267A] cursor-pointer"
                  >
                    <span className="uppercase">{t("get_the_app")}</span>
                  </div>
                </div>

                <div
                  className={`absolute z-[1] opacity-30 w-[350px] h-[350px] rotate-[-12.65deg] ${
                    isAr ? "left-9" : "right-9"
                  } bottom-[-20px]`}
                >
                  <img
                    src="/images/sublogo.png"
                    className="w-full h-full"
                    alt=""
                  />
                </div>
              </div> */}
                        </div>

                        {/* Filters */}
                        <div className="hidden md:flex max-w-[1180px] mx-auto mt-10 md:px-0">
                            <EventFilteration />
                        </div>

                        {/* Events */}
                        <SectionV2
                            visibleCountProp={100}
                            events={eventsPayload}
                            layout="grid"
                            //@ts-ignore

                            loading={isLoading || isFetching}
                        />
                    </div>
                </div>
            </MainLayout>

            {showAppModal && (
                <AppDownloadModal
                    open={showAppModal}
                    onClose={() => setShowAppModal(false)}
                />
            )}
        </>
    )
}

export default DiscoverPage
